import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Printer, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";
import { letterheadHTML } from "./Letterhead";

const PRIO = { high: "عالية", medium: "متوسطة", low: "منخفضة" };
const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const fmtC = (c) => `${String(c.created_at).slice(0, 16).replace("T", " ")} — ${c.author_name}: ${c.text}`;

const withComments = (tasks) => Promise.all(tasks.map(async (t) => {
  if (!t.comment_count) return { ...t, comments: [] };
  try { return { ...t, comments: (await api.taskComments(t.id)).items || [] }; } catch { return { ...t, comments: [] }; }
}));

export const TasksExport = ({ tasks, personName, tabLabel, company }) => {
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    if (!tasks.length) { toast.warning("لا توجد مهام للتصدير"); return; }
    const win = kind === "pdf" ? window.open("", "_blank") : null;
    if (kind === "pdf" && !win) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    setBusy(kind);
    try {
      const list = await withComments(tasks);
      if (kind === "xlsx") {
        const ExcelJS = (await import("exceljs")).default;
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("المهام", { views: [{ rightToLeft: true }] });
        ws.columns = [["#", 5], ["المهمة", 30], ["الوصف", 40], ["الأولوية", 10], ["الاستحقاق", 13], ["الحالة", 10], ["المكلّفون", 30], ["أنشأها", 18], ["أنجزها", 18], ["التعليقات", 60]].map(([header, width]) => ({ header, width }));
        ws.getRow(1).eachCell((c) => { c.font = { bold: true, color: { argb: "FFFFFFFF" } }; c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } }; });
        list.forEach((t, i) => {
          const r = ws.addRow([i + 1, t.title, t.description || "", PRIO[t.priority] || "", t.due_date || "", t.status === "done" ? `مكتملة${t.review ? ` (${t.review.rating}/5)` : ""}` : t.status === "review" ? "بانتظار الاعتماد" : "جارية",
            (t.assignees || []).map((a) => a.name).join("، "), t.created_by_name || "", t.completed_by_name || "", t.comments.map(fmtC).join("\n")]);
          r.alignment = { wrapText: true, vertical: "top" };
        });
        const buf = await wb.xlsx.writeBuffer();
        await saveBlob(new Blob([buf]), `مهام_${personName}.xlsx`);
      } else {
        const rows = list.map((t, i) => `<tr><td>${i + 1}</td><td class="r"><b>${esc(t.title)}</b>${t.description ? `<div class="d">${esc(t.description)}</div>` : ""}
          ${t.comments.length ? `<div class="cm">${t.comments.map((c) => `<div>• ${esc(fmtC(c))}</div>`).join("")}</div>` : ""}</td>
          <td>${PRIO[t.priority] || ""}</td><td>${esc(t.due_date || "—")}</td><td>${t.status === "done" ? `✓ مكتملة${t.review ? ` (${t.review.rating}/5)` : ""}` : t.status === "review" ? "بانتظار الاعتماد" : "جارية"}</td><td>${esc(t.created_by_name || "")}</td></tr>`).join("");
        win.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>مهام ${esc(personName)}</title><style>
          @page{size:A4;margin:12mm}body{font-family:Tahoma,Arial;color:#0f172a;font-size:12.5px}h1{font-size:19px;margin:6px 0}
          table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border:1px solid #e2e8f0;padding:6px;text-align:center;vertical-align:top}th{background:#eff6ff;color:#1d4ed8}
          .r{text-align:right}.d{color:#64748b;font-size:11.5px;margin-top:2px;white-space:pre-wrap}.cm{margin-top:4px;padding:4px 6px;background:#f8fafc;border-radius:6px;font-size:11px;color:#334155}
          </style></head><body>${letterheadHTML(company || {})}<h1>مهام الموظف: ${esc(personName)}</h1>
          <div>${esc(tabLabel)} · عدد المهام: ${list.length} · المكتملة: ${list.filter((t) => t.status === "done").length} · تاريخ التقرير: ${new Date().toISOString().slice(0, 10)}</div>
          <table><thead><tr><th>#</th><th>المهمة والتعليقات</th><th>الأولوية</th><th>الاستحقاق</th><th>الحالة</th><th>أنشأها</th></tr></thead><tbody>${rows}</tbody></table>
          <script>window.onload=()=>window.print()</script></body></html>`);
        win.document.close();
      }
    } catch { toast.error("تعذّر التصدير"); win?.close(); } finally { setBusy(""); }
  };
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" data-testid="tasks-export-xlsx" disabled={!!busy} onClick={() => run("xlsx")} className="h-9 gap-1.5 text-emerald-700 border-emerald-200">
        {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel</Button>
      <Button size="sm" variant="outline" data-testid="tasks-export-pdf" disabled={!!busy} onClick={() => run("pdf")} className="h-9 gap-1.5">
        {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} PDF</Button>
    </div>
  );
};
